/**
 * Word (DOCX) 解析器 — 使用 mammoth.js 转 HTML 再 block 化
 * 对齐 features.md 1.4：Word 导入
 */

import type { Block } from '@hump/shared-types';
import type { Parser, ParseResult } from './types';
import { createBlock } from './block-builder';

export class DocxParser implements Parser {
  readonly sourceType = 'docx' as const;

  async parse(input: ArrayBuffer): Promise<ParseResult> {
    const mammoth = (await import('mammoth')).default;
    const result = await mammoth.convertToHtml({ arrayBuffer: input });

    // 用 DOMParser 解析 HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(result.value, 'text/html');
    const body = doc.body;

    const blocks: Block[] = [];
    let order = 0;
    let title = 'Untitled';

    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_ELEMENT, {
      acceptNode(node: Element) {
        const tag = node.tagName.toLowerCase();
        if (tag === 'h1' || tag === 'h2' || tag === 'h3' ||
            tag === 'p' || tag === 'blockquote' || tag === 'pre' || tag === 'ul' || tag === 'ol') {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      },
    });

    const elements: Element[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      elements.push(node as Element);
    }

    for (const el of elements) {
      const tag = el.tagName.toLowerCase();
      const text = (el.textContent || '').trim();
      if (!text) continue;

      let type: Block['type'] = 'paragraph';
      let level: number | undefined;

      if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
        type = 'heading';
        level = tag === 'h1' ? 1 : tag === 'h2' ? 2 : 3;
        if (title === 'Untitled' && level === 1) title = text;
      } else if (tag === 'blockquote') {
        type = 'quote';
      } else if (tag === 'pre') {
        type = 'code';
      } else if (tag === 'ul' || tag === 'ol') {
        type = 'list';
      }

      blocks.push(createBlock({ docId: '', order: order++, type, level, original: text }));
    }

    return { blocks, meta: { title, sourceType: 'docx', lang: 'en' } };
  }
}
